import * as jsPDF from 'jspdf';
import { AQCF_LOGO_BASE64 } from '../../../core/dialogs/preview-frame-dialog/aqcf-logo';

export function generateSynthesePdf(
  rows: any[],
  frameName: string,
  start: string,
  end: string,
  enterpriseName: string,
  frameColors: any[] = []
): void {
  if (!rows || rows.length === 0) return;

  // ── Extraction & agrégation des données ──────────────────────────────────────

  const auditMap = new Map<number, any>();

  for (const row of rows) {
    const id = Number(row.AuditId);
    if (!auditMap.has(id)) {
      auditMap.set(id, {
        auditId: id,
        user: row.User || '—',
        entreprise: row.NomEntreprise || enterpriseName,
        internalCode: row.internal_code || row.int_nbr_customer_id || '—',
        startedAt: row.started_at || '',
        globalNote: null,
        ncCrit: 0, ncMaj: 0, ncMin: 0,
        seenItems: new Set<number>(),
      });
    }
    const audit = auditMap.get(id);
    if (String(row.typeNote) === '0' && audit.globalNote === null) {
      audit.globalNote = parseFloat(row.ValeurNote) || 0;
    }
    if (row.IdItem && !audit.seenItems.has(Number(row.IdItem))) {
      audit.seenItems.add(Number(row.IdItem));
      const ev = (row.EvaluationItem || '').toUpperCase();
      const ko = (row.KoConforme || '').toUpperCase() === 'KO';
      if (ko || ev === 'NCCRI') audit.ncCrit++;
      else if (ev === 'NCMAJ') audit.ncMaj++;
      else if (ev === 'NCMIN' || ev === 'NCMOD') audit.ncMin++;
    }
  }

  // Notes par chapitre (typeNote='2') et par indicateur (typeNote='1')
  const buildNoteMap = (typeNote: string) => {
    const m = new Map<string, { vals: number[]; min: number }>();
    const seen = new Set<string>();
    for (const row of rows) {
      if (String(row.typeNote) === typeNote && row.NomNote) {
        const key = `${row.AuditId}_${row.NomNote}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const val = parseFloat(row.ValeurNote) || 0;
        if (!m.has(row.NomNote)) m.set(row.NomNote, { vals: [], min: 100 });
        const c = m.get(row.NomNote)!;
        c.vals.push(val);
        if (val < c.min) c.min = val;
      }
    }
    return m;
  };

  const chapMap  = buildNoteMap('2');
  const indicMap = buildNoteMap('1');

  // NC regroupées par critère
  const buildNcMap = (rows2: any[], test: (row: any) => boolean) => {
    const m = new Map<string, { item: string; auditIds: Set<number> }>();
    for (const row of rows2) {
      if (!row.IdItem || !test(row)) continue;
      const key = row.DetailItem || '—';
      if (!m.has(key)) m.set(key, { item: key, auditIds: new Set() });
      m.get(key).auditIds.add(Number(row.AuditId));
    }
    return Array.from(m.values()).sort((a, b) => b.auditIds.size - a.auditIds.size);
  };

  const ncCritList = buildNcMap(rows, r => (r.KoConforme || '').toUpperCase() === 'KO' || (r.EvaluationItem || '').toUpperCase() === 'NCCRI');
  const ncMajList  = buildNcMap(rows, r => (r.EvaluationItem || '').toUpperCase() === 'NCMAJ');
  const ncMinList  = buildNcMap(rows, r => ['NCMIN', 'NCMOD'].includes((r.EvaluationItem || '').toUpperCase()));

  const audits = Array.from(auditMap.values()).sort((a, b) => (a.globalNote || 0) - (b.globalNote || 0));

  // KPIs globaux
  const nbAudits = audits.length;
  const notes    = audits.map(a => a.globalNote || 0).filter(n => n > 0);
  const avgNote  = notes.length ? notes.reduce((s, n) => s + n, 0) / notes.length : 0;
  const minNote  = notes.length ? Math.min(...notes) : 0;
  const maxNote  = notes.length ? Math.max(...notes) : 0;
  const totalNcCrit = audits.reduce((s, a) => s + a.ncCrit, 0);
  const totalNcMaj  = audits.reduce((s, a) => s + a.ncMaj, 0);
  const totalNcMin  = audits.reduce((s, a) => s + a.ncMin, 0);
  const nbOk        = audits.filter(a => (a.globalNote || 0) >= 90).length;
  const nbVigilance = audits.filter(a => (a.globalNote || 0) >= 85 && (a.globalNote || 0) < 90).length;
  const nbCritique  = audits.filter(a => (a.globalNote || 0) < 85).length;

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const hexToRgb = (hex: string): number[] => {
    const h = (hex || '#888888').replace('#', '');
    return [parseInt(h.substr(0, 2), 16) || 128, parseInt(h.substr(2, 2), 16) || 128, parseInt(h.substr(4, 2), 16) || 128];
  };

  // Seuils triés du plus haut au plus bas (ex: OK 90-100, VIGILANCE 85-89, CRITIQUE 0-84)
  const sortedColors = frameColors.length
    ? frameColors.slice().sort((a: any, b: any) => b.value_min - a.value_min)
    : [];

  const getStatus = (n: number): string => {
    if (sortedColors.length) {
      for (const fc of sortedColors) {
        if (n >= fc.value_min) return fc.name || 'N/A';
      }
      return sortedColors[sortedColors.length - 1].name || 'N/A';
    }
    return n >= 90 ? 'OK' : n >= 85 ? 'VIGILANCE' : 'CRITIQUE';
  };

  const statusRgb = (s: string): number[] => {
    if (sortedColors.length) {
      const fc = sortedColors.find((c: any) => c.name === s);
      if (fc) return hexToRgb(fc.back_color);
    }
    return s === 'OK' ? [46, 125, 50] : s === 'VIGILANCE' ? [200, 130, 0] : [198, 40, 40];
  };

  // buildThemeList ici car getStatus est nécessaire pour les statusCounts
  const buildThemeList = (m: Map<string, { vals: number[]; min: number }>) =>
    Array.from(m.entries())
      .map(([nom, d]) => {
        const statusCounts: {[k: string]: number} = {};
        for (const v of d.vals) {
          const st = getStatus(v);
          statusCounts[st] = (statusCounts[st] || 0) + 1;
        }
        return {
          nom,
          avg: d.vals.reduce((s, v) => s + v, 0) / d.vals.length,
          min: d.min,
          nb: d.vals.length,
          statusCounts,
        };
      })
      .sort((a, b) => a.avg - b.avg);

  const chapList  = buildThemeList(chapMap);
  const indicList = buildThemeList(indicMap);

  const fmtDate = (d: string) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? d.slice(0, 10) : dt.toLocaleDateString('fr-FR');
  };
  const fmtPct = (n: number) => String(Math.round(n * 10) / 10);
  const trunc  = (s: string, max: number) => s && s.length > max ? s.substring(0, max - 1) + '…' : (s || '—');

  // ── jsPDF setup ──────────────────────────────────────────────────────────────

  const pdf = new (jsPDF as any)('p', 'mm', 'a4');
  const PW = 210, PH = 297, M = 12, CW = PW - 2 * M;
  const HH = 18, FH = 20;
  let y = 0;

  const NAVY   = [13, 49, 96];
  const ORANGE = [230, 100, 20];
  const RED    = [198, 40, 40];
  const LGRAY  = [220, 220, 220];

  const f  = (r: number, g: number, b: number) => pdf.setFillColor(r, g, b);
  const sd = (r: number, g: number, b: number) => pdf.setDrawColor(r, g, b);
  const co = (r: number, g: number, b: number) => pdf.setTextColor(r, g, b);
  const ty = (sz: number, bold: boolean)        => { pdf.setFontSize(sz); pdf.setFontStyle(bold ? 'bold' : 'normal'); };
  const bx = (x: number, yy: number, w: number, h: number, mode = 'F') => pdf.rect(x, yy, w, h, mode);
  const ln = (x1: number, y1: number, x2: number, y2: number) => pdf.line(x1, y1, x2, y2);

  let currentPage = 0;
  let sectionTitle = frameName || 'Synthèse Audits';
  const dateGenStr = new Date().toLocaleDateString('fr-FR');

  const drawHeader = () => {
    currentPage++;
    f(NAVY[0], NAVY[1], NAVY[2]); bx(0, 0, PW, HH - 2, 'F');
    f(ORANGE[0], ORANGE[1], ORANGE[2]); bx(0, HH - 2, PW, 2, 'F');
    pdf.addImage(AQCF_LOGO_BASE64, 'JPEG', 2, 1, 28, 14);
    ty(9, true); co(255, 255, 255);
    pdf.text(sectionTitle, 42, 8);
    ty(6, false); co(160, 190, 220);
    pdf.text('Généré le : ' + dateGenStr, 42, 13.5);
    // Le numéro de page (X / Y) est ajouté en post-traitement
    ty(7, true); pdf.setFontStyle('bolditalic'); co(ORANGE[0], ORANGE[1], ORANGE[2]);
    pdf.text('Confidentiel', PW - 3, 14.5, { align: 'right' });
  };

  const drawFooter = (showConfidential = false) => {
    const FY = PH - FH;
    if (showConfidential) {
      f(255, 255, 255); bx(0, FY, PW, 6, 'F');
      sd(RED[0], RED[1], RED[2]); pdf.setLineWidth(0.3);
      ln(0, FY, PW, FY); ln(0, FY + 6, PW, FY + 6);
      ty(7, true); co(RED[0], RED[1], RED[2]);
      pdf.text('Document strictement confidentiel — Usage interne AQCF et du client uniquement', PW / 2, FY + 4.5, { align: 'center' });
    }
    f(NAVY[0], NAVY[1], NAVY[2]); bx(0, FY + 6, PW, 14, 'F');
    ty(5, false); co(190, 205, 225);
    pdf.text(
      'SAS AQCF - Agro Quality Consulting France  |  Capital : 300 000 €  |  3 Rue d\'Amsterdam, Z.A. des Vallées, 60110 Amblainville  |  SIREN : 478 926 520  |  SIRET : 478 926 520 00048  |  RCS Beauvais  |  NAF : 7022Z',
      PW / 2, FY + 10, { align: 'center' }
    );
    pdf.text(
      'Tel : 03.44.52.15.46  |  Fax : 03.44.22.75.68  |  info@aqcf.eu  |  www.aqcf.eu  |  Document strictement confidentiel — Généré le ' + dateGenStr,
      PW / 2, FY + 15, { align: 'center' }
    );
  };

  const addPage = (title?: string) => {
    if (title) sectionTitle = title;
    pdf.addPage(); drawHeader(); drawFooter();
    y = HH + 5;
  };

  const needPage = (h: number, title?: string): boolean => {
    if (y + h > PH - FH - 5) { addPage(title); return true; }
    return false;
  };

  const chapterBand = (label: string) => {
    f(NAVY[0], NAVY[1], NAVY[2]); bx(M, y, CW, 7, 'F');
    ty(10, true); co(255, 255, 255);
    pdf.text(label, M + 3, y + 5);
    y += 10;
  };

  const sectionLine = (label: string) => {
    f(ORANGE[0], ORANGE[1], ORANGE[2]); bx(M, y, 2, 6, 'F');
    ty(9, true); co(NAVY[0], NAVY[1], NAVY[2]);
    pdf.text(label, M + 4, y + 5);
    y += 9;
  };

  // ── PAGE 1 — Couverture ───────────────────────────────────────────────────────

  f(NAVY[0], NAVY[1], NAVY[2]); bx(0, 0, PW, 80, 'F');
  f(ORANGE[0], ORANGE[1], ORANGE[2]); bx(0, 80, PW, 3, 'F');
  pdf.addImage(AQCF_LOGO_BASE64, 'JPEG', M, 8, 35, 18);

  ty(22, true); co(255, 255, 255);
  pdf.text('Synthèse Audits Hygiène', PW / 2, 50, { align: 'center' });
  ty(11, false); co(190, 210, 235);
  pdf.text('Audits de conformité hygiène & sécurité alimentaire', PW / 2, 59, { align: 'center' });
  pdf.setFontStyle('bolditalic'); ty(10, false); co(ORANGE[0], ORANGE[1], ORANGE[2]);
  pdf.text('— Confidentiel —', PW / 2, 68, { align: 'center' });

  // Carte entreprise
  const cardY = 95;
  sd(LGRAY[0], LGRAY[1], LGRAY[2]); pdf.setLineWidth(0.3);
  bx(M, cardY, CW, 72, 'S');
  ty(16, true); co(NAVY[0], NAVY[1], NAVY[2]);
  pdf.text(trunc(frameName || enterpriseName, 40), PW / 2, cardY + 18, { align: 'center' });
  ln(M + 10, cardY + 24, M + CW - 10, cardY + 24);
  ty(10, false); co(120, 120, 120);
  pdf.text(`Période : ${fmtDate(start)} — ${fmtDate(end)}`, PW / 2, cardY + 34, { align: 'center' });

  // KPI boxes (couverture)
  const kpiY = cardY + 44;
  const kpiW = CW / 3;
  [
    { label: 'AUDITS RÉALISÉS',   value: String(nbAudits) },
    { label: 'NOTE MOYENNE',      value: fmtPct(avgNote) },
    { label: 'NC CRITIQUES (KO)', value: String(totalNcCrit) },
  ].forEach((kpi, i) => {
    const kx = M + i * kpiW;
    // Séparateur vertical uniquement entre les boxes
    if (i > 0) {
      sd(LGRAY[0], LGRAY[1], LGRAY[2]); pdf.setLineWidth(0.5);
      ln(kx, kpiY, kx, kpiY + 22);
    }
    ty(6, false); co(120, 120, 120);
    pdf.text(kpi.label, kx + 4, kpiY + 6);
    ty(16, true); co(NAVY[0], NAVY[1], NAVY[2]);
    pdf.text(kpi.value, kx + 4, kpiY + 16);
  });

  drawFooter(true);
  currentPage++;

  // ── PAGE 2 — Synthèse globale ────────────────────────────────────────────────

  sectionTitle = 'Synthèse globale';
  pdf.addPage(); drawHeader(); drawFooter();
  y = HH + 5;

  chapterBand('Chapitre 1 — Synthèse globale');

  // 1.1 Vue d'ensemble
  sectionLine('1.1 Vue d\'ensemble');

  const kpi2W = (CW - 6) / 4;
  [
    { label: 'AUDITS RÉALISÉS',   value: String(nbAudits),    sub: '' },
    { label: 'NOTE MOYENNE',      value: fmtPct(avgNote),     sub: `min ${fmtPct(minNote)}\nmax ${fmtPct(maxNote)}` },
    { label: 'NC CRITIQUES (KO)', value: String(totalNcCrit), sub: 'Non-conformités critiques' },
    { label: 'NC MAJEURES',       value: String(totalNcMaj),  sub: 'Non-conformités majeures' },
  ].forEach((kpi, i) => {
    const kx = M + i * (kpi2W + 2);
    sd(LGRAY[0], LGRAY[1], LGRAY[2]); pdf.setLineWidth(0.5);
    ln(kx, y, kx, y + 26);
    ty(6, false); co(120, 120, 120); pdf.text(kpi.label, kx + 3, y + 5);
    ty(14, true); co(NAVY[0], NAVY[1], NAVY[2]); pdf.text(kpi.value, kx + 3, y + 15);
    if (kpi.sub) {
      ty(6, false); co(120, 120, 120);
      kpi.sub.split('\n').forEach((sl, si) => pdf.text(sl, kx + 3, y + 20 + si * 4));
    }
  });
  y += 32;

  // 1.2 Répartition NC
  sectionLine('1.2 Répartition des conclusions — toutes non-conformités (hors NO / SO)');

  ty(8, true); co(60, 60, 60);
  pdf.text('Non-conformités identifiées :', M, y + 4);
  y += 8;

  const barArea = CW - 68;
  const totalForBar = Math.max(totalNcCrit + totalNcMaj + totalNcMin, 1);
  [
    { label: 'NC Critiques (KO)', count: totalNcCrit, rgb: RED,           nbEtab: audits.filter(a => a.ncCrit > 0).length },
    { label: 'NC Majeures',       count: totalNcMaj,  rgb: [200, 130, 0], nbEtab: audits.filter(a => a.ncMaj > 0).length },
    { label: 'NC Mineures',       count: totalNcMin,  rgb: [100, 140, 180] as any, nbEtab: audits.filter(a => a.ncMin > 0).length },
  ].forEach(bar => {
    const bw = Math.max((bar.count / totalForBar) * barArea, 6);
    ty(7, false); co(80, 80, 80);
    pdf.text(bar.label, M + 50, y + 4.5, { align: 'right' });
    f(bar.rgb[0], bar.rgb[1], bar.rgb[2]); bx(M + 52, y, bw, 6, 'F');
    ty(7, true); co(255, 255, 255);
    pdf.text(String(bar.count), M + 54, y + 4.5);
    ty(7, false); co(80, 80, 80);
    pdf.text(`${bar.count} NC (${bar.nbEtab} étab.)`, M + 52 + bw + 3, y + 4.5);
    y += 9;
  });

  y += 4;
  ty(8, true); co(60, 60, 60);
  pdf.text('Statut des établissements audités :', M, y + 4);
  y += 8;

  const statusBars = sortedColors.length
    ? sortedColors.map((fc: any) => ({
        label: `${fc.name} (${fc.value_min}–${fc.value_max !== undefined ? fc.value_max : 100}%)`,
        count: audits.filter(a => getStatus(a.globalNote || 0) === fc.name).length,
        rgb: hexToRgb(fc.back_color),
      }))
    : [
        { label: 'Audits OK (>= 90%)',        count: nbOk,        rgb: [46, 125, 50] as number[] },
        { label: 'Audits VIGILANCE (85–89%)',  count: nbVigilance, rgb: [200, 130, 0] as number[] },
        { label: 'Audits CRITIQUE (< 85%)',    count: nbCritique,  rgb: RED as number[] },
      ];

  statusBars.forEach(bar => {
    const pct = nbAudits > 0 ? Math.round(bar.count / nbAudits * 100) : 0;
    const bw2 = Math.max((bar.count / Math.max(nbAudits, 1)) * (barArea - 20), 5);
    ty(7, false); co(80, 80, 80);
    pdf.text(bar.label, M + 67, y + 4.5, { align: 'right' });
    f(bar.rgb[0], bar.rgb[1], bar.rgb[2]); bx(M + 69, y, bw2, 6, 'F');
    if (bar.count > 0) {
      ty(7, true); co(255, 255, 255);
      pdf.text(String(bar.count), M + 71, y + 4.5);
    }
    ty(7, false); co(80, 80, 80);
    pdf.text(`${pct}% (${bar.count}/${nbAudits})`, M + 69 + bw2 + 3, y + 4.5);
    y += 9;
  });

  // 1.3 Établissements en alerte
  y += 4;
  needPage(40);
  sectionLine('1.3 Établissements en alerte — classés par note croissante (pires en premier)');

  ty(6, false); pdf.setFontStyle('italic'); co(100, 100, 100);
  pdf.text('NC Crit. = Non-Conformités Critiques (KO)  |  NC Maj. = Non-Conformités Majeures', M, y + 3);
  y += 7;

  audits.filter(a => (a.globalNote || 0) < 90).forEach(a => {
    needPage(10);
    const st = getStatus(a.globalNote || 0);
    const sc = statusRgb(st);
    f(sc[0], sc[1], sc[2]); bx(M, y, 20, 6, 'F');
    ty(6, true); co(255, 255, 255); pdf.text(st, M + 1, y + 4.5);
    ty(7, false); co(60, 60, 60);
    const nc = [
      a.ncCrit > 0 ? `${a.ncCrit} NC Crit.` : '',
      a.ncMaj  > 0 ? `${a.ncMaj} NC Maj.`  : '',
      a.ncMin  > 0 ? `${a.ncMin} NC Min.`  : '',
    ].filter(Boolean).join(' | ');
    pdf.text(`${trunc(a.entreprise, 48)} — Note ${fmtPct(a.globalNote || 0)} (${nc || 'Aucune NC'})`, M + 22, y + 4);
    ty(6.5, false); co(120, 120, 120);
    pdf.text(`Audit #${a.auditId}  |  ${fmtDate(a.startedAt)}`, M + 22, y + 9);
    y += 12;
  });

  // ── PAGE 3 — Résultats par établissement ─────────────────────────────────────

  addPage('Résultats par établissement');
  chapterBand('Chapitre 2 — Résultats par établissement');
  sectionLine('2. Note globale et non-conformités par établissement');

  ty(6, false); pdf.setFontStyle('italic'); co(100, 100, 100);
  const seuilsLabel = sortedColors.length
    ? sortedColors.map((fc: any) => `${fc.name} : ${fc.value_min}–${fc.value_max !== undefined ? fc.value_max : 100}%`).join('  |  ')
    : 'Seuil CRITIQUE : < 85%  |  Seuil VIGILANCE : < 90%';
  pdf.text(seuilsLabel, M, y + 3);
  y += 7;

  const tCols = [
    { label: 'Établissement', w: 50 },
    { label: 'Date audit',    w: 22 },
    { label: 'Auditeur',      w: 30 },
    { label: 'Note',          w: 14 },
    { label: 'NC Crit.',      w: 14 },
    { label: 'NC Maj.',       w: 14 },
    { label: 'NC Autres',     w: 14 },
    { label: 'Statut',        w: CW - 158 },
  ];
  const tRowH = 8;

  const drawTblHeader = () => {
    f(NAVY[0], NAVY[1], NAVY[2]); bx(M, y, CW, tRowH, 'F');
    ty(7, true); co(255, 255, 255);
    let cx = M + 1;
    tCols.forEach(c => { pdf.text(c.label, cx, y + 5.5); cx += c.w; });
    y += tRowH;
  };
  drawTblHeader();

  audits.slice(0, 25).forEach((a, ri) => {
    if (needPage(tRowH + 2, 'Résultats par établissement')) { drawTblHeader(); }
    const st = getStatus(a.globalNote || 0);
    const sc = statusRgb(st);
    if (ri % 2 === 0) { f(245, 248, 252); bx(M, y, CW, tRowH, 'F'); }
    let cx = M + 1;
    [
      trunc(a.entreprise, 29),
      fmtDate(a.startedAt),
      trunc(a.user, 18),
      fmtPct(a.globalNote || 0),
      a.ncCrit > 0 ? String(a.ncCrit) : '—',
      a.ncMaj  > 0 ? String(a.ncMaj)  : '—',
      a.ncMin  > 0 ? String(a.ncMin)  : '—',
    ].forEach((cell, i) => {
      if (i === 3) {
        const nc = statusRgb(st);
        ty(7, true); co(nc[0], nc[1], nc[2]);
      } else { ty(6, false); co(60, 60, 60); }
      pdf.text(cell, cx, y + 5.5);
      cx += tCols[i].w;
    });
    f(sc[0], sc[1], sc[2]); bx(cx, y + 1.5, tCols[7].w - 2, tRowH - 3, 'F');
    ty(6, true); co(255, 255, 255); pdf.text(st, cx + 1, y + 5.5);
    y += tRowH;
  });

  // ── PAGES 4-5 — Non-conformités ──────────────────────────────────────────────

  addPage('Non-conformités — Détail');
  chapterBand('Chapitre 3 — Non-conformités — Détail');

  const drawNcSection = (title: string, list: any[], headerRgb: number[]) => {
    needPage(22);
    sectionLine(title);

    if (list.length === 0) {
      ty(8, false); co(120, 120, 120);
      pdf.text('Aucune non-conformité identifiée.', M + 4, y + 4);
      y += 10; return;
    }

    const C0 = 70; // largeur colonne critère
    const C1 = 22; // largeur colonne nb audits
    const C2 = CW - C0 - C1; // largeur colonne établissements
    const ncLineH = 5;
    const ncPadV  = 4;

    // En-tête
    f(headerRgb[0], headerRgb[1], headerRgb[2]); bx(M, y, CW, 8, 'F');
    ty(8, true); co(255, 255, 255);
    pdf.text('Critère non-conforme', M + 2, y + 5.5);
    pdf.text('Nb audits', M + C0 + 2, y + 5.5);
    pdf.text('Établissements impactés', M + C0 + C1 + 2, y + 5.5);
    y += 8;

    list.forEach((nc, ri) => {
      const sortedAudits = Array.from(nc.auditIds)
        .map((id: number) => auditMap.get(id))
        .filter((a: any) => !!a)
        .sort((a: any, b: any) => (a.globalNote || 0) - (b.globalNote || 0));
      const maxShown = 10;
      const shown = sortedAudits.slice(0, maxShown);
      const overflow = sortedAudits.length - maxShown;

      const critLines: string[] = pdf.splitTextToSize(nc.item, C0 - 10);
      const entLines: string[] = [];
      shown.forEach((a: any) => {
        const label = `• ${trunc(a.entreprise, 40)} — ${fmtPct(a.globalNote || 0)}`;
        const lines: string[] = pdf.splitTextToSize(label, C2 - 4);
        lines.forEach((l: string) => entLines.push(l));
      });
      if (overflow > 0) entLines.push(`  + ${overflow} autre(s)…`);

      const rh = Math.max(10, Math.max(critLines.length, entLines.length) * ncLineH + ncPadV * 2);
      needPage(rh + 2, 'Non-conformités — Détail');
      const rowBg = ri % 2 === 0 ? 250 : 255;

      // Fond ligne complète
      f(rowBg, rowBg, rowBg); bx(M, y, CW, rh, 'F');

      // Critère (dans sa colonne)
      ty(8, false); co(40, 40, 40);
      pdf.text(critLines, M + 2, y + ncPadV + 3);

      // Fond colonnes droites (masque tout débordement du critère)
      f(rowBg, rowBg, rowBg); bx(M + C0, y, C1 + C2, rh, 'F');

      // Nb audits
      ty(9, true); co(headerRgb[0], headerRgb[1], headerRgb[2]);
      pdf.text(String(nc.auditIds.size), M + C0 + C1 / 2, y + ncPadV + 3, { align: 'center' });

      // Fond colonne établissements (masque débordement du nb audits)
      f(rowBg, rowBg, rowBg); bx(M + C0 + C1, y, C2, rh, 'F');

      // Établissements
      ty(7, false); co(60, 60, 60);
      pdf.text(entLines, M + C0 + C1 + 2, y + ncPadV + 3);

      // Séparateur bas
      f(220, 220, 220); bx(M, y + rh - 0.3, CW, 0.3, 'F');
      y += rh;
    });
    y += 6;
  };

  drawNcSection(
    `3.1 Non-conformités CRITIQUES (KO) — ${ncCritList.length} item(s) distinct(s)`,
    ncCritList, RED
  );
  drawNcSection(
    `3.2 Non-conformités MAJEURES — ${ncMajList.length} item(s) distinct(s)`,
    ncMajList, [200, 100, 0]
  );
  drawNcSection(
    `3.3 Non-conformités MINEURES — ${ncMinList.length} item(s) distinct(s)`,
    ncMinList, NAVY
  );

  // ── PAGE FINALE — Analyse par thème ──────────────────────────────────────────

  // Statuts triés du pire au meilleur pour affichage dans tables (inverse de sortedColors)
  const statusLabels: Array<{ name: string; rgb: number[] }> = sortedColors.length
    ? sortedColors.slice().reverse().map((fc: any) => ({ name: fc.name, rgb: hexToRgb(fc.back_color) }))
    : [
        { name: 'CRITIQUE',  rgb: RED as number[] },
        { name: 'VIGILANCE', rgb: [200, 130, 0] as number[] },
        { name: 'OK',        rgb: [46, 125, 50] as number[] },
      ];

  const drawThemeBlock = (
    list: any[],
    titleTable: string,
    titleBars: string,
    titleDistrib: string,
    typeLabel: string
  ) => {
    if (list.length === 0) {
      ty(8, false); co(120, 120, 120);
      pdf.text(`Aucune donnée de ${typeLabel.toLowerCase()} disponible.`, M + 4, y + 4);
      y += 10;
      return;
    }

    // Table des notes
    sectionLine(titleTable);
    const thCols = [
      { label: typeLabel,    w: 97 },
      { label: 'Note moy.', w: 22 },
      { label: 'Note min.', w: 22 },
      { label: 'Nb audits', w: 20 },
      { label: 'Niveau',    w: CW - 161 },
    ];
    f(NAVY[0], NAVY[1], NAVY[2]); bx(M, y, CW, 8, 'F');
    ty(7, true); co(255, 255, 255);
    let cx = M + 1;
    thCols.forEach(c => { pdf.text(c.label, cx, y + 5.5); cx += c.w; });
    y += 8;

    list.forEach((c, ri) => {
      needPage(10, 'Analyse par thème');
      const st = getStatus(c.avg);
      const sc = statusRgb(st);
      if (ri % 2 === 0) { f(245, 248, 252); bx(M, y, CW, 8, 'F'); }
      let cx2 = M + 1;
      [trunc(c.nom, 55), fmtPct(c.avg), fmtPct(c.min), String(c.nb)].forEach((val, i) => {
        if (i === 1) { ty(7, true); co(sc[0], sc[1], sc[2]); }
        else { ty(7, false); co(60, 60, 60); }
        pdf.text(val, cx2, y + 5.5);
        cx2 += thCols[i].w;
      });
      f(sc[0], sc[1], sc[2]); bx(cx2, y + 1.5, thCols[4].w - 4, 5, 'F');
      ty(6, true); co(255, 255, 255); pdf.text(st, cx2 + 1, y + 5.5);
      y += 8;
    });
    y += 6;

    // Profil barres
    needPage(list.length * 9 + 20, 'Analyse par thème');
    sectionLine(titleBars);
    const labelW = 80;
    const barStartX = M + labelW + 3;
    const profileBarW = CW - labelW - 3;
    list.forEach(c => {
      needPage(9, 'Analyse par thème');
      ty(6.5, false); co(60, 60, 60);
      pdf.text(trunc(c.nom, 42), M + 1, y + 5);
      const bw = Math.max((c.avg / 100) * profileBarW, 5);
      const sc = statusRgb(getStatus(c.avg));
      f(sc[0], sc[1], sc[2]); bx(barStartX, y + 0.5, bw, 7, 'F');
      const pctLabel = fmtPct(c.avg);
      if (bw > 18) {
        ty(6.5, true); co(255, 255, 255);
        pdf.text(pctLabel, barStartX + bw - 2, y + 5.5, { align: 'right' });
      } else {
        ty(6.5, false); co(60, 60, 60);
        pdf.text(pctLabel, barStartX + bw + 2, y + 5.5);
      }
      y += 9;
    });
    y += 6;

    // Distribution des statuts par thème
    needPage(30, 'Analyse par thème');
    sectionLine(titleDistrib);

    const statusColW = Math.floor((CW - 100) / statusLabels.length);
    const distribCols = [
      { label: typeLabel, w: 100 },
      ...statusLabels.map(s => ({ label: s.name, w: statusColW })),
    ];
    f(NAVY[0], NAVY[1], NAVY[2]); bx(M, y, CW, 8, 'F');
    ty(7, true); co(255, 255, 255);
    let cx3 = M + 1;
    distribCols.forEach((c, i) => {
      if (i === 0) {
        pdf.text(c.label, cx3, y + 5.5);
      } else {
        const sc = statusLabels[i - 1].rgb;
        f(sc[0], sc[1], sc[2]); bx(cx3 - 1, y + 1, c.w - 1, 6, 'F');
        ty(6, true); co(255, 255, 255);
        pdf.text(c.label, cx3, y + 5.5);
        ty(7, true); co(255, 255, 255);
      }
      cx3 += c.w;
    });
    y += 8;

    list.forEach((c, ri) => {
      needPage(9, 'Analyse par thème');
      if (ri % 2 === 0) { f(245, 248, 252); bx(M, y, CW, 8, 'F'); }
      ty(7, false); co(60, 60, 60);
      pdf.text(trunc(c.nom, 56), M + 1, y + 5.5);
      let cx4 = M + 1 + distribCols[0].w;
      statusLabels.forEach((sl, si) => {
        const count = c.statusCounts[sl.name] || 0;
        const sc = sl.rgb;
        if (count > 0) {
          ty(7, true); co(sc[0], sc[1], sc[2]);
          pdf.text(String(count), cx4, y + 5.5);
        } else {
          ty(7, false); co(180, 180, 180);
          pdf.text('—', cx4, y + 5.5);
        }
        cx4 += distribCols[si + 1].w;
      });
      y += 8;
    });
    y += 6;
  };

  addPage('Analyse par thème');
  chapterBand('Chapitre 4 — Analyse par thème');

  drawThemeBlock(
    chapList,
    '4.1 Chapitres — notes moyennes (du plus faible au plus fort)',
    '4.2 Profil des notes par chapitre',
    '4.3 Distribution des statuts par chapitre',
    'Chapitre'
  );

  drawThemeBlock(
    indicList,
    '4.4 Indicateurs — notes moyennes (du plus faible au plus fort)',
    '4.5 Profil des notes par indicateur',
    '4.6 Distribution des statuts par indicateur',
    'Indicateur'
  );

  // ── Numérotation finale "Page X / Y" sur toutes les pages avec en-tête ────────

  const totalPages = (pdf.internal as any).getNumberOfPages();
  for (let p = 2; p <= totalPages; p++) {
    pdf.setPage(p);
    // Recouvrir la zone droite de l'en-tête avec du navy puis réécrire
    f(NAVY[0], NAVY[1], NAVY[2]); bx(PW - 46, 0, 46, HH - 2, 'F');
    ty(7, false); co(200, 215, 235);
    pdf.text(`Page ${p - 1} / ${totalPages - 1}`, PW - 3, 9, { align: 'right' });
    ty(7, true); pdf.setFontStyle('bolditalic'); co(ORANGE[0], ORANGE[1], ORANGE[2]);
    pdf.text('Confidentiel', PW - 3, 14.5, { align: 'right' });
  }

  // ── Sauvegarde ───────────────────────────────────────────────────────────────

  const safeName = (frameName || enterpriseName || 'audits').replace(/[^a-zA-Z0-9\-_]/g, '_');
  pdf.save(`synthese-audits-${safeName}.pdf`);
}



// WEBPACK FOOTER //
// /home/web_clients/qualicheck.fr/preprod/erp/src/app/pages/audits/audit-list/audit-synthese-pdf.ts